import React, { useContext, useEffect, useState } from "react"
import { PageProps, graphql } from "gatsby"
import { Flex } from "rebass"
import { css, useTheme } from "@emotion/react"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import { routeParams, routes } from "../../routes"
import {
  CountryPicker,
  SUPPORTED_COUNTRIES,
} from "../../components/CountryPicker"
import { translationKeys } from "../../util/translationKeys"
import { RestaurantListGhost } from "../../components/ghost-state/RestaurantListGhost"
import StoreContext from "../../context/store-context"
import { loadIntercom } from "../../util/load-intercom"
import { Layout } from "../../components/Layout"
import { Heading2 } from "../../components/typography/headings"
import { BodyTextRegular } from "../../components/typography/text"
import { ButtonPrimary } from "../../components/button/buttons"
import { RestaurantCard } from "../../components/restaurantCard/RestaurantCard"
import { SEO } from "../../components/SEO"
import { setMomentLocale } from "../../util/util-moment-locale"
import { InitialCollectionMethodModal } from "../../components/InitialCollectionMethodModal"
import { initialCollectionMethodChoiceEnabled } from "../../util/initial-delivery-method-choice-enabled"
import { CollectionMethodIdentifiers } from "../../util/collection-method-identifiers"
import { RestaurantListItem } from "../../models/restaurant"
import { useFetchRestaurants } from "../../services/queries"

const RestaurantsPage = ({ params }: PageProps) => {
  const { navigate, changeLanguage, language } = useI18next()
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)
  const [redirectionVenueId, setRedirectionVenueId] = useState("")
  const { clearCart, initialCollectionMethodChosen } = useContext(StoreContext)
  const { data: restaurants, isLoading: restaurantsLoading } =
    useFetchRestaurants()

  const theme = useTheme()

  const restaurantCardsContainerStyle = css`
    display: grid;
    gap: 16px;
    margin-bottom: 32px;
    ${theme.bp.desktop} {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    ${theme.bp.mobile} {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
  `

  const bookTableButtonStyle = css`
    ${theme.bp.desktop} {
      width: 200px;
    }
    ${theme.bp.mobile} {
      width: 100%;
    }
  `

  useEffect(() => {
    loadIntercom()
  }, [])

  useEffect(() => {
    setMomentLocale(language)
  }, [language])

  useEffect(() => {
    // if browser language is same as gatsby-plugin-react-i18next default language,
    // it will serve a route without locale
    // this is a way to force the route that includes the locale
    if (language === "en" && !window.location.pathname.includes("/en")) {
      changeLanguage("en")
      return
    }

    // checks if country-code is supported
    const countryCode = params[routeParams.countryCode]
    if (!SUPPORTED_COUNTRIES.map((c) => c.code).includes(countryCode)) {
      navigate(routes.notFound)
    }
  }, [params[routeParams.countryCode]])

  useEffect(() => {
    clearCart() // TODO cart
  }, [])

  const handleVenueSelection = ({
    title,
    slug,
    id,
    ...rest
  }: RestaurantListItem) => {
    const courierOption = rest.shop_collection_methods.find(
      (method) => method === CollectionMethodIdentifiers.courier
    )
    const pickUpOption = rest.shop_collection_methods.find(
      (method) => method === CollectionMethodIdentifiers.pickup
    )

    const deliveryAndPickUpEnabled = courierOption && pickUpOption

    if (window.analytics) {
      window.analytics.track("Venue selected", { title })
    }

    if (!deliveryAndPickUpEnabled) {
      navigate(routes.restaurant(params[routeParams.countryCode], slug || id))
      return
    }

    if (
      initialCollectionMethodChoiceEnabled &&
      !initialCollectionMethodChosen
    ) {
      setModalOpen(true)
      setRedirectionVenueId(slug || id)
    } else {
      navigate(routes.restaurant(params[routeParams.countryCode], slug || id))
    }
  }

  const onBookTableClick = () => {
    window.open(process.env.GATSBY_TABLE_BOOKING_URL_PARTNER, "_blank")
  }

  return (
    <Layout countryCode={params[routeParams.countryCode]}>
      <Flex
        flexDirection="column"
        alignItems="center"
        overflowY="scroll"
        overflowX="hidden"
        height="100%"
        flex={["1"]}
      >
        <Flex
          sx={{
            width: "100%",
            maxWidth: ["", "1300px"],
            flexDirection: "column",
            paddingLeft: ["20px", "20px", "100px"],
            paddingRight: ["20px", "20px", "100px"],
          }}
        >
          <Flex
            flexDirection={["column", "row"]}
            justifyContent="space-between"
            alignItems="center"
            my="24px"
          >
            <Flex marginBottom={["16px", "0px"]} flexDirection="column">
              <Heading2 mb="8px">
                {t(translationKeys.comboOrderChoose)}
              </Heading2>
              <BodyTextRegular color={theme.colors.primary}>
                {t(translationKeys.comboOrderChooseSubtext)}
              </BodyTextRegular>
              {/* TODO enable when API is ready */}
              {false && (
                <CountryPicker countryCode={params[routeParams.countryCode]} />
              )}
            </Flex>
            {process.env.GATSBY_TABLE_BOOKING_URL_PARTNER && (
              <ButtonPrimary
                backgroundColor={theme.colors.ctaMain}
                hoverColor={theme.colors.ctaSecondaryOne}
                color={theme.colors.white}
                onClick={onBookTableClick}
                css={bookTableButtonStyle}
              >
                {t(translationKeys.homeBookTable)}
              </ButtonPrimary>
            )}
          </Flex>
          <div css={restaurantCardsContainerStyle}>
            {restaurantsLoading || !restaurants ? (
              <RestaurantListGhost />
            ) : (
              restaurants?.map((v, i) => (
                <RestaurantCard
                  // For datadog testing
                  className={`res-card-${i}`}
                  key={i}
                  onClick={() => handleVenueSelection(v)}
                  restaurant={v}
                />
              ))
            )}
          </div>
          {modalOpen && (
            <InitialCollectionMethodModal
              onClick={() =>
                navigate(
                  routes.restaurant(
                    params[routeParams.countryCode],
                    redirectionVenueId
                  )
                )
              }
            />
          )}
        </Flex>
      </Flex>
    </Layout>
  )
}

export default RestaurantsPage

export const Head = () => (
  <SEO
    title={process.env.GATSBY_PARTNER_TITLE || ""}
    description={process.env.GATSBY_PARTNER_DESCRIPTION || ""}
    image={process.env.GATSBY_LOGO || ""}
  />
)

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
